/** Шаблоны сервера: сохранение, загрузка, перезапись, переименование, удаление. */
import { api, type FolderInput, type GuildData, type TemplateFolder, type TemplateMeta } from '../api.js';
import { app } from '../ctx.js';
import { h, icon, plural } from '../dom.js';
import { store } from '../store.js';
import { closePopover, confirmDialog, modal, popover, promptDialog, toast } from '../ui/overlay.js';
import { roleNames, rolePicker } from '../ui/roles.js';
import { relative } from '../preview/time.js';
import { isDraftEmpty } from '../../../src/shared/transform.js';

async function refreshList() {
  if (!app.guild) return;
  const [templates, folders] = await Promise.all([api.templates(app.guild.id), api.folders(app.guild.id)]);
  app.guild.templates = templates;
  app.guild.folders = folders;
}

/** Выбранная в окне шаблонов папка: 'all' - все, null - без папки, иначе id папки. */
let currentFolder: string | null | 'all' = 'all';

const folderOf = (g: GuildData, id: string | null | undefined) => (id ? g.folders.find((f) => f.id === id) ?? null : null);
const folderLabel = (f: TemplateFolder) => `${f.emoji ? `${f.emoji} ` : ''}${f.name}`;
/** Можно ли менять шаблон (папки «только просмотр» - нельзя). */
export function canEditTemplate(g: GuildData, t: { folderId: string | null }) {
  const f = folderOf(g, t.folderId);
  return !f || f.canEdit;
}

export async function saveAsNew(folderId: string | null = null): Promise<boolean> {
  const g = app.guild!;
  const folder = folderOf(g, folderId);
  const title = folder ? `Новый шаблон в «${folder.name}»` : 'Новый шаблон';
  const name = await promptDialog(title, 'Название', store.templateName ? `${store.templateName} (копия)` : '', 'Сохранить');
  if (!name) return false;
  try {
    const t = await api.createTemplate(g.id, name, store.draft, folder?.id ?? null);
    await refreshList();
    store.setMeta({ templateId: t.id, templateName: t.name });
    toast(`Шаблон «${t.name}» сохранён`, 'ok');
    return true;
  } catch (err: any) {
    toast(err.message, 'error');
    return false;
  }
}

export async function saveCurrentTemplate(): Promise<void> {
  const g = app.guild!;
  const meta = store.templateId ? g.templates.find((t) => t.id === store.templateId) : null;
  if (!store.templateId || (meta && !canEditTemplate(g, meta))) {
    if (meta) toast('Шаблон в папке только для просмотра - сохраните копию', 'info');
    await saveAsNew();
    return;
  }
  try {
    await api.updateTemplate(g.id, store.templateId, { data: store.draft });
    await refreshList();
    toast(`Шаблон «${store.templateName}» обновлён`, 'ok');
  } catch (err: any) {
    toast(err.message, 'error');
  }
}

export async function loadTemplate(id: string, askConfirm = true): Promise<void> {
  const g = app.guild!;
  if (askConfirm && !isDraftEmpty(store.draft) && !(await confirmDialog('Загрузить шаблон?', 'Текущий черновик будет заменён. Отменить можно через Ctrl+Z.', 'Загрузить'))) return;
  try {
    const t = await api.template(g.id, id);
    store.replace(t.data, { templateId: t.id, templateName: t.name, editTarget: null });
    toast(t.canEdit === false ? `Загружен шаблон «${t.name}» (только просмотр)` : `Загружен шаблон «${t.name}»`, 'ok');
  } catch (err: any) {
    toast(err.message, 'error');
  }
}


/* ---------- окно шаблонов ---------- */

function accessSummary(g: GuildData, f: TemplateFolder): string {
  const view = f.viewRoles === null ? 'все с доступом' : f.viewRoles.length ? roleNames(g.roles, f.viewRoles) : 'только управляющие';
  const edit = f.editRoles === null ? 'все, кто видит' : f.editRoles.length ? roleNames(g.roles, f.editRoles) : 'только управляющие';
  return `Видят: ${view} · Редактируют: ${edit}`;
}

const compact = <T>(xs: (T | null | undefined)[]) => xs.filter(Boolean) as T[];
const isRestricted = (f: TemplateFolder) => f.viewRoles !== null || f.editRoles !== null;

export function openTemplatesModal() {
  const g = app.guild!;
  if (currentFolder !== 'all' && currentFolder !== null && !folderOf(g, currentFolder)) currentFolder = 'all';
  const m = modal({ title: 'Шаблоны сервера', icon: 'folder', wide: true, subtitle: 'Общие для всех, у кого есть доступ к редактору. Папки могут ограничивать, кто видит и редактирует шаблоны' });
  m.el.classList.add('tpl-modal');
  const search = h('input', { class: 'input', placeholder: 'Поиск по названию...', attrs: { 'aria-label': 'Поиск шаблонов' } });
  const sidebar = h('nav', { class: 'tpl-folders', attrs: { 'aria-label': 'Папки' } });
  const head = h('div', { class: 'tpl-folder-head' });
  const list = h('div', { class: 'tpl-list' });
  const countLabel = h('span', { class: 'muted small grow' });

  const rerender = async () => {
    try {
      await refreshList();
    } catch (err: any) {
      toast(err.message, 'error');
    }
    render();
  };

  /* --- перенос в папку --- */
  const moveMenu = (anchor: HTMLElement, t: TemplateMeta) => {
    const targets: { id: string | null; label: string; emoji: string | null }[] = [
      { id: null, label: 'Без папки', emoji: null },
      ...g.folders.filter((f) => f.canEdit).map((f) => ({ id: f.id, label: f.name, emoji: f.emoji })),
    ];
    popover(
      anchor,
      h(
        'div',
        { class: 'menu-list' },
        h('div', { class: 'menu-title' }, 'Переместить в папку'),
        ...targets.map((x) =>
          h(
            'button',
            {
              class: ['menu-item', x.id === t.folderId && 'active'],
              type: 'button',
              disabled: x.id === t.folderId,
              onclick: async () => {
                closePopover();
                try {
                  await api.updateTemplate(g.id, t.id, { folderId: x.id });
                  toast(`«${t.name}» -> ${x.label}`, 'ok');
                  await rerender();
                } catch (err: any) {
                  toast(err.message, 'error');
                }
              },
            },
            x.emoji ? h('span', { class: 'tpl-folder-emoji' }, x.emoji) : icon(x.id ? 'folder' : 'layers', 15),
            h('span', { class: 'grow' }, x.label),
            x.id === t.folderId ? icon('check', 15) : null,
          ),
        ),
      ),
      { width: 240 },
    );
  };

  /* --- строка шаблона --- */
  const item = (t: TemplateMeta) => {
    const editable = canEditTemplate(g, t);
    const folder = folderOf(g, t.folderId);
    const actions: HTMLElement[] = [
      h(
        'button',
        {
          class: 'btn btn-sm btn-primary',
          type: 'button',
          onclick: async () => {
            m.close();
            await loadTemplate(t.id);
          },
        },
        'Открыть',
      ),
    ];
    if (editable) {
      actions.push(
        h(
          'button',
          {
            class: 'icon-btn',
            type: 'button',
            title: 'Перезаписать текущим черновиком',
            onclick: async () => {
              if (!(await confirmDialog('Перезаписать шаблон?', `Содержимое «${t.name}» заменится текущим черновиком.`, 'Перезаписать'))) return;
              try {
                await api.updateTemplate(g.id, t.id, { data: store.draft });
                store.setMeta({ templateId: t.id, templateName: t.name });
                toast('Шаблон перезаписан', 'ok');
                await rerender();
              } catch (err: any) {
                toast(err.message, 'error');
              }
            },
          },
          icon('save', 16),
        ),
      );
    }
    actions.push(
      h(
        'button',
        {
          class: 'icon-btn',
          type: 'button',
          title: 'Скопировать ссылку на шаблон (для !appeal и других ботов)',
          onclick: async () => {
            const link = `${location.origin}/#/g/${g.id}/editor?tpl=${t.id}`;
            try {
              await navigator.clipboard.writeText(link);
              toast('Ссылка на шаблон скопирована', 'ok');
            } catch {
              await promptDialog('Ссылка на шаблон', 'Скопируйте вручную', link);
            }
          },
        },
        icon('link', 16),
      ),
    );
    if (editable) {
      const moveBtn: HTMLButtonElement = h('button', { class: 'icon-btn', type: 'button', title: 'Переместить в папку', onclick: () => moveMenu(moveBtn, t) }, icon('folderMove', 16));
      actions.push(
        moveBtn,
        h(
          'button',
          {
            class: 'icon-btn',
            type: 'button',
            title: 'Переименовать',
            onclick: async () => {
              const name = await promptDialog('Переименовать шаблон', 'Новое название', t.name);
              if (!name || name === t.name) return;
              try {
                await api.updateTemplate(g.id, t.id, { name });
                if (store.templateId === t.id) store.setMeta({ templateName: name });
                await rerender();
              } catch (err: any) {
                toast(err.message, 'error');
              }
            },
          },
          icon('edit', 16),
        ),
        h(
          'button',
          {
            class: 'icon-btn danger',
            type: 'button',
            title: 'Удалить',
            onclick: async () => {
              if (!(await confirmDialog('Удалить шаблон?', `«${t.name}» исчезнет для всего сервера. Кнопки, которые отвечают этим шаблоном, перестанут работать.`, 'Удалить', true))) return;
              try {
                await api.deleteTemplate(g.id, t.id);
                if (store.templateId === t.id) store.setMeta({ templateId: null, templateName: null });
                await rerender();
              } catch (err: any) {
                toast(err.message, 'error');
              }
            },
          },
          icon('trash', 16),
        ),
      );
    }
    return h(
      'div',
      { class: ['tpl-item', t.id === store.templateId && 'is-current'] },
      h('div', { class: 'tpl-icon' }, icon(t.mode === 'v2' ? 'box' : 'layers', 18)),
      h(
        'div',
        { class: 'tpl-meta' },
        h(
          'div',
          { class: 'tpl-name' },
          h('span', { class: 'tpl-name-text' }, t.name),
          t.id === store.templateId ? h('span', { class: 'badge badge-accent' }, 'открыт') : null,
          !editable ? h('span', { class: 'badge', title: 'В этой папке у вас только просмотр' }, icon('eye', 12), 'просмотр') : null,
        ),
        h(
          'div',
          { class: 'muted small' },
          currentFolder === 'all' && folder ? h('span', { class: 'tpl-folder-chip' }, folderLabel(folder)) : null,
          `${t.mode === 'v2' ? 'Components V2' : 'Классический'} · изменён ${relative(new Date(t.updatedAt))}`,
        ),
      ),
      h('div', { class: 'tpl-actions' }, ...actions),
    );
  };

  /* --- боковая панель папок --- */
  const folderBtn = (key: string | null | 'all', label: string, count: number, iconName: string, f?: TemplateFolder) =>
    h(
      'button',
      {
        class: ['tpl-folder', currentFolder === key && 'active'],
        type: 'button',
        title: f ? accessSummary(g, f) : undefined,
        onclick: () => {
          currentFolder = key;
          render();
        },
      },
      f?.emoji ? h('span', { class: 'tpl-folder-emoji' }, f.emoji) : icon(iconName, 16),
      h('span', { class: 'tpl-folder-name' }, label),
      f && isRestricted(f) ? h('span', { class: 'tpl-folder-flag', title: 'Доступ ограничен' }, icon('lock', 12)) : null,
      f && !f.canEdit ? h('span', { class: 'tpl-folder-flag', title: 'Только просмотр' }, icon('eye', 12)) : null,
      h('span', { class: 'tpl-folder-count' }, String(count)),
    );

  const renderSidebar = () => {
    const unfiled = g.templates.filter((t) => !folderOf(g, t.folderId)).length;
    sidebar.replaceChildren(
      ...compact([folderBtn('all', 'Все шаблоны', g.templates.length, 'grid'),
      folderBtn(null, 'Без папки', unfiled, 'layers'),
      g.folders.length ? h('div', { class: 'tpl-folders-sep' }, 'Папки') : null,
      ...g.folders.map((f) => folderBtn(f.id, f.name, g.templates.filter((t) => t.folderId === f.id).length, 'folder', f)),
      g.canManageFolders
        ? h(
            'button',
            { class: 'tpl-folder tpl-folder-add', type: 'button', onclick: () => folderDialog(null) },
            icon('plus', 16),
            h('span', { class: 'tpl-folder-name' }, 'Новая папка'),
          )
        : null,
    ]));
  };

  const renderHead = () => {
    const f = currentFolder !== 'all' && currentFolder !== null ? folderOf(g, currentFolder) : null;
    if (!f) {
      head.replaceChildren();
      head.hidden = true;
      return;
    }
    head.hidden = false;
    head.replaceChildren(
      ...compact([h('div', { class: 'tpl-folder-head-icon' }, f.emoji ? h('span', null, f.emoji) : icon('folder', 18)),
      h('div', { class: 'grow' }, h('div', { class: 'tpl-folder-head-name' }, f.name, !f.canEdit ? h('span', { class: 'badge' }, icon('eye', 12), 'только просмотр') : null), h('div', { class: 'muted small' }, accessSummary(g, f))),
      g.canManageFolders ? h('button', { class: 'btn btn-sm', type: 'button', onclick: () => folderDialog(f) }, icon('settings', 15), 'Настроить') : null,
    ]));
  };

  const render = () => {
    renderSidebar();
    renderHead();
    const q = search.value.trim().toLowerCase();
    const inFolder = g.templates.filter((t) => (currentFolder === 'all' ? true : currentFolder === null ? !folderOf(g, t.folderId) : t.folderId === currentFolder));
    const items = inFolder.filter((t) => !q || t.name.toLowerCase().includes(q));
    list.replaceChildren(
      ...(items.length
        ? items.map(item)
        : [
            h(
              'div',
              { class: 'empty-hint' },
              icon('folder', 16),
              inFolder.length ? 'Ничего не найдено' : g.templates.length ? 'В этой папке пока пусто' : 'Шаблонов пока нет - сохраните текущий черновик',
            ),
          ]),
    );
    countLabel.textContent = `${inFolder.length} ${plural(inFolder.length, 'шаблон', 'шаблона', 'шаблонов')} · /template send, /template preview`;
    const target = currentFolder !== 'all' && currentFolder !== null ? folderOf(g, currentFolder) : null;
    saveBtn.disabled = !!target && !target.canEdit;
    saveBtn.title = saveBtn.disabled ? 'В этой папке у вас только просмотр' : target ? `Сохранить в «${target.name}»` : '';
    if (updateBtn) {
      const meta = g.templates.find((t) => t.id === store.templateId);
      updateBtn.hidden = !!meta && !canEditTemplate(g, meta);
    }
  };

  /* --- настройки папки --- */
  const folderDialog = (f: TemplateFolder | null) => {
    const d = modal({ title: f ? `Папка «${f.name}»` : 'Новая папка', icon: 'folder', subtitle: 'Название и доступ. Управляющие папками видят и редактируют всё' });
    d.el.classList.add('folder-modal');
    const name = h('input', { class: 'input', value: f?.name ?? '', maxLength: 50, placeholder: 'Например: Ивенты', attrs: { 'aria-label': 'Название папки' } });
    const emoji = h('input', { class: 'input folder-emoji-input', value: f?.emoji ?? '', maxLength: 8, placeholder: '📁', attrs: { 'aria-label': 'Эмодзи' } });

    type Mode = 'all' | 'roles' | 'none';
    const modeOf = (r: string[] | null): Mode => (r === null ? 'all' : r.length ? 'roles' : 'none');
    const accessBlock = (title: string, hint: string, initial: string[] | null, allLabel: string) => {
      const state = { mode: modeOf(initial), roles: new Set(initial ?? []) };
      const picker = rolePicker(g.roles, state.roles, { compact: true });
      const seg = h('div', { class: 'segmented' });
      const paint = () => {
        seg.replaceChildren(
          ...(
            [
              ['all', allLabel],
              ['roles', 'Выбранные роли'],
              ['none', 'Только управляющие'],
            ] as [Mode, string][]
          ).map(([k, label]) =>
            h(
              'button',
              {
                class: ['seg', state.mode === k && 'active'],
                type: 'button',
                onclick: () => {
                  state.mode = k;
                  paint();
                },
              },
              label,
            ),
          ),
        );
        picker.hidden = state.mode !== 'roles';
      };
      paint();
      const el = h('div', { class: 'field folder-access' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, title)), h('p', { class: 'muted small' }, hint), seg, picker);
      const value = (): string[] | null => (state.mode === 'all' ? null : state.mode === 'none' ? [] : [...state.roles]);
      return { el, value };
    };
    const view = accessBlock('Кто видит', 'Видеть - открывать, отправлять и копировать шаблоны из папки (в панели и через /template).', f ? f.viewRoles : null, 'Все с доступом');
    const edit = accessBlock('Кто редактирует', 'Создавать, перезаписывать, переименовывать, перемещать и удалять шаблоны. Эти роли папку тоже видят.', f ? f.editRoles : null, 'Все, кто видит');

    d.body.append(
      h('div', { class: 'folder-name-row' }, h('label', { class: 'field' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, 'Эмодзи')), emoji), h('label', { class: 'field grow' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, 'Название')), name)),
      view.el,
      edit.el,
    );

    const save = h(
      'button',
      {
        class: 'btn btn-primary',
        type: 'button',
        onclick: async () => {
          const input: FolderInput = { name: name.value.trim(), emoji: emoji.value.trim() || null, viewRoles: view.value(), editRoles: edit.value() };
          if (!input.name) {
            toast('Укажите название папки', 'error');
            name.focus();
            return;
          }
          save.disabled = true;
          try {
            const saved = f ? await api.updateFolder(g.id, f.id, input) : await api.createFolder(g.id, input);
            toast(f ? 'Папка сохранена' : `Папка «${saved.name}» создана`, 'ok');
            d.close();
            if (!f) currentFolder = saved.id;
            await rerender();
          } catch (err: any) {
            toast(err.message, 'error');
          } finally {
            save.disabled = false;
          }
        },
      },
      icon('save', 16),
      f ? 'Сохранить' : 'Создать',
    );
    d.footer.append(
      ...compact([f
        ? h(
            'button',
            {
              class: 'btn btn-danger',
              type: 'button',
              onclick: async () => {
                const n = g.templates.filter((t) => t.folderId === f.id).length;
                const text = n
                  ? `${n} ${plural(n, 'шаблон', 'шаблона', 'шаблонов')} из папки ${n === 1 ? 'переедет' : 'переедут'} в «Без папки» и ${n === 1 ? 'станет доступен' : 'станут доступны'} всем, у кого есть доступ к редактору.`
                  : 'Папка пустая.';
                if (!(await confirmDialog(`Удалить папку «${f.name}»?`, text, 'Удалить', true))) return;
                try {
                  await api.deleteFolder(g.id, f.id);
                  d.close();
                  currentFolder = 'all';
                  toast('Папка удалена', 'ok');
                  await rerender();
                } catch (err: any) {
                  toast(err.message, 'error');
                }
              },
            },
            icon('trash', 16),
            'Удалить папку',
          )
        : null,
      h('div', { class: 'grow' }),
      h('button', { class: 'btn', type: 'button', onclick: () => d.close() }, 'Отмена'),
      save,
    ]));
  };

  search.addEventListener('input', render);

  const updateBtn = store.templateId
    ? h(
        'button',
        {
          class: 'btn',
          type: 'button',
          onclick: async () => {
            await saveCurrentTemplate();
            render();
          },
        },
        icon('save', 16),
        `Обновить «${store.templateName}»`,
      )
    : null;
  const saveBtn = h(
    'button',
    {
      class: 'btn btn-primary',
      type: 'button',
      onclick: async () => {
        const target = currentFolder !== 'all' && currentFolder !== null ? currentFolder : null;
        if (await saveAsNew(target)) render();
      },
    },
    icon('plus', 16),
    'Сохранить текущий',
  );

  m.body.append(h('div', { class: 'tpl-layout' }, sidebar, h('div', { class: 'tpl-main' }, h('div', { class: 'tpl-toolbar' }, search), head, list)));
  m.footer.append(...([countLabel, updateBtn, saveBtn].filter(Boolean) as HTMLElement[]));
  render();
}
